/**
 * StructuredChat — chat surface for claude_code / codex instances.
 *
 * Two modes, picked by the instance's workspace configuration:
 *   - Local-spawn:   subscribes to /api/v1/instances/:id/events/stream
 *                    (cloud SSE; events come from the go-agent's
 *                     canonical AgentEvent pipeline).
 *   - Remote-share:  opens the WebSocket directly to the vendor share
 *                    URL fetched from /api/v1/instances/:id/share-url.
 *                    Frames are normalized via normalizeVendorFrame.
 *
 * Send path is mode-specific too:
 *   - Local: POST /api/v1/agents/:agentId/command (queues agent_message)
 *   - Remote: write a vendor message frame into the open WebSocket.
 *
 * v1 keeps the renderer minimal: text bubbles, tool-call cards, errors.
 * Permission-prompt approval UI is stubbed (Phase E).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import {
  normalizeCanonical,
  normalizeVendorFrame,
  type UICard,
} from '../lib/eventNormalizer';

type PermissionState = 'pending' | 'approved' | 'denied' | 'sending';

interface StructuredChatProps {
  instanceId: string;
  agentId: string;
  sessionId: string | null;
  /** When set, the chat operates in remote-attach mode using this URL. */
  shareUrl?: string | null;
  /** Called when the chat starts a new session (user clicked New Chat). */
  onNewSession?: () => Promise<string | null>;
  /** Called when the user picks a different session from the switcher. */
  onSwitchSession?: (sessionId: string) => Promise<void>;
}

interface ArchivedSession {
  session_id: string;
  event_count: number;
  last_event_at: number;
  is_active: boolean;
}

export default function StructuredChat({
  instanceId,
  agentId,
  sessionId,
  shareUrl,
  onNewSession,
  onSwitchSession,
}: StructuredChatProps) {
  const { getHeaders } = useApiHeaders();
  const [cards, setCards] = useState<UICard[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<Record<string, PermissionState>>({});
  const [sessions, setSessions] = useState<ArchivedSession[]>([]);
  const seqRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const sseRef = useRef<EventSource | null>(null);

  const mode: 'local' | 'remote' = shareUrl ? 'remote' : 'local';

  // ── Subscribe (mode-specific) ───────────────────────────────────────────
  useEffect(() => {
    setCards([]);
    seqRef.current = 0;
    setError(null);

    let cancelled = false;

    async function subscribeLocal() {
      if (!sessionId) return;
      const headers = await getHeaders();
      // EventSource doesn't accept custom headers in the browser; rely
      // on Cookie-based auth OR fall back to fetch+ReadableStream if
      // the API endpoint requires Bearer. Console's APIClient uses
      // Bearer, so we use fetch-with-ReadableStream here.
      const ctrl = new AbortController();
      try {
        const res = await fetch(
          `${API_URL}/api/v1/instances/${instanceId}/events/stream?session_id=${encodeURIComponent(sessionId)}&after_seq=0`,
          { headers, signal: ctrl.signal },
        );
        if (!res.ok || !res.body) {
          setError(`Event stream failed: ${res.status}`);
          return;
        }
        setConnected(true);
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          // SSE events are separated by blank lines.
          const parts = buf.split('\n\n');
          buf = parts.pop() ?? '';
          for (const part of parts) {
            for (const line of part.split('\n')) {
              if (!line.startsWith('data: ')) continue;
              const payload = line.slice(6).trim();
              if (!payload) continue;
              try {
                const ev = JSON.parse(payload);
                const card = normalizeCanonical(ev);
                if (card) {
                  seqRef.current = Math.max(seqRef.current, card.seq + 1);
                  setCards(prev => [...prev, card]);
                }
              } catch {
                // ignore malformed
              }
            }
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message ?? e));
      } finally {
        setConnected(false);
      }

      return () => ctrl.abort();
    }

    async function subscribeRemote() {
      if (!shareUrl) return;
      try {
        // shareUrl is already a ws:// or wss:// (or http(s)://, which
        // we convert). We never log or persist this URL.
        const wsUrl = shareUrl.replace(/^http/, 'ws');
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen = () => setConnected(true);
        ws.onclose = () => setConnected(false);
        ws.onerror = () => { if (!cancelled) setError('Share session WebSocket error'); };
        ws.onmessage = (msg) => {
          try {
            const raw = JSON.parse(typeof msg.data === 'string' ? msg.data : '');
            const card = normalizeVendorFrame(raw, seqRef.current++);
            if (card) setCards(prev => [...prev, card]);
          } catch {
            // ignore
          }
        };
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message ?? e));
      }
    }

    if (mode === 'remote') {
      void subscribeRemote();
    } else {
      void subscribeLocal();
    }

    return () => {
      cancelled = true;
      sseRef.current?.close();
      wsRef.current?.close();
      sseRef.current = null;
      wsRef.current = null;
    };
  }, [instanceId, sessionId, shareUrl, mode, getHeaders]);

  // ── Send (mode-specific) ────────────────────────────────────────────────
  const send = useCallback(async () => {
    const message = input.trim();
    if (!message || sending) return;
    setSending(true);
    setInput('');

    // Optimistic local echo.
    setCards(prev => [...prev, { kind: 'message', role: 'user', text: message, seq: seqRef.current++ }]);

    try {
      if (mode === 'remote') {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          throw new Error('Remote share session not connected');
        }
        ws.send(JSON.stringify({ type: 'user_message', text: message }));
      } else {
        const headers = await getHeaders();
        const res = await fetch(`${API_URL}/api/v1/agents/${agentId}/command`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            command_type: 'agent_message',
            command_data: {
              instance_id: instanceId,
              session_id: sessionId,
              message,
            },
          }),
        });
        if (!res.ok) {
          throw new Error(`send failed: ${res.status}`);
        }
      }
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setSending(false);
    }
  }, [agentId, getHeaders, input, instanceId, mode, sending, sessionId]);

  const onKey = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }, [send]);

  const decidePermission = useCallback(async (toolUseId: string, approved: boolean) => {
    setPermissionState(s => ({ ...s, [toolUseId]: 'sending' }));
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/agents/${agentId}/command`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command_type: 'permission_decision',
          command_data: { tool_use_id: toolUseId, approved },
        }),
      });
      if (!res.ok) throw new Error(`decision failed: ${res.status}`);
      setPermissionState(s => ({ ...s, [toolUseId]: approved ? 'approved' : 'denied' }));
    } catch (e: any) {
      setError(String(e?.message ?? e));
      setPermissionState(s => ({ ...s, [toolUseId]: 'pending' }));
    }
  }, [agentId, getHeaders]);

  const refreshSessions = useCallback(async () => {
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/sessions`, { headers });
      if (!res.ok) return;
      const data = await res.json() as { sessions?: ArchivedSession[] };
      setSessions(data.sessions ?? []);
    } catch {
      // non-fatal
    }
  }, [instanceId, getHeaders]);

  useEffect(() => { void refreshSessions(); }, [refreshSessions, sessionId]);

  const handleNewChat = useCallback(async () => {
    if (!onNewSession) return;
    await onNewSession();
    setCards([]);
    seqRef.current = 0;
    void refreshSessions();
  }, [onNewSession, refreshSessions]);

  const handleSwitch = useCallback(async (sid: string) => {
    if (!onSwitchSession || sid === sessionId) return;
    await onSwitchSession(sid);
    setCards([]);
    seqRef.current = 0;
  }, [onSwitchSession, sessionId]);

  const rendered = useMemo(() => cards, [cards]);

  return (
    <div className="flex flex-col h-full">
      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400">
          <span className={`inline-block w-2 h-2 rounded-full ${connected ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700'}`} />
          <span>{mode === 'remote' ? 'Remote share' : 'Local'} · {connected ? 'connected' : 'disconnected'}</span>
          {sessions.length > 1 && onSwitchSession ? (
            <select
              value={sessionId ?? ''}
              onChange={(e) => void handleSwitch(e.target.value)}
              className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 cursor-pointer"
            >
              {sessions.map(s => (
                <option key={s.session_id} value={s.session_id}>
                  {s.session_id.slice(0, 8)} · {s.event_count} events
                </option>
              ))}
            </select>
          ) : (
            sessionId && <span className="font-mono text-[10px] text-slate-400">session: {sessionId.slice(0, 8)}</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleNewChat}
          disabled={!onNewSession}
          className="text-xs px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
          New chat
        </button>
      </div>

      {/* Event stream */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {error && (
          <div className="p-3 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}
        {rendered.map((c) => (
          <Card
            key={`${c.kind}-${c.seq}`}
            c={c}
            permissionState={c.kind === 'permission_request' && c.toolUseId ? permissionState[c.toolUseId] : undefined}
            onDecide={decidePermission}
          />
        ))}
        {rendered.length === 0 && !error && (
          <div className="text-sm text-slate-400 dark:text-slate-600 text-center mt-10">
            No messages yet. Say something to start the conversation.
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 dark:border-slate-800 p-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder="Message Claude Code…"
          rows={2}
          className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none"
          disabled={sending}
        />
        <div className="flex justify-end mt-2">
          <button
            type="button"
            onClick={() => void send()}
            disabled={!input.trim() || sending}
            className="px-4 py-1.5 text-sm rounded-xl text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed cursor-pointer"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Card({
  c,
  permissionState,
  onDecide,
}: {
  c: UICard;
  permissionState?: PermissionState;
  onDecide: (toolUseId: string, approved: boolean) => void;
}) {
  switch (c.kind) {
    case 'message':
      return (
        <div className={`flex ${c.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
            c.role === 'user'
              ? 'bg-primary-600 text-white'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
          }`}>
            {c.text}
          </div>
        </div>
      );
    case 'tool_use':
      return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-3">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">🔧 {c.name}</div>
          <pre className="text-[11px] font-mono text-slate-500 dark:text-slate-500 overflow-x-auto">{prettyJSON(c.input)}</pre>
        </div>
      );
    case 'tool_result':
      return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 p-3 ml-4">
          <div className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1">← result</div>
          <pre className="text-[11px] font-mono text-slate-500 dark:text-slate-500 overflow-x-auto max-h-32">{prettyJSON(c.content)}</pre>
        </div>
      );
    case 'permission_request': {
      const decided = permissionState === 'approved' || permissionState === 'denied';
      const sending = permissionState === 'sending';
      return (
        <div className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20 p-3">
          <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2">
            Permission requested{c.name ? `: ${c.name}` : ''}
          </div>
          {c.input != null && (
            <pre className="text-[11px] font-mono text-amber-700 dark:text-amber-500 overflow-x-auto max-h-32 mb-2 whitespace-pre-wrap">{prettyJSON(c.input)}</pre>
          )}
          {!c.toolUseId ? (
            <div className="text-[11px] text-amber-600 dark:text-amber-500">No tool id — cannot approve from the UI.</div>
          ) : decided ? (
            <div className="text-[11px] font-medium text-amber-700 dark:text-amber-500">
              {permissionState === 'approved' ? '✓ Approved' : '✗ Denied'}
            </div>
          ) : (
            <div className="flex space-x-2">
              <button
                type="button"
                disabled={sending}
                onClick={() => onDecide(c.toolUseId!, true)}
                className="px-3 py-1 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Approve
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={() => onDecide(c.toolUseId!, false)}
                className="px-3 py-1 text-xs rounded-lg bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                Deny
              </button>
            </div>
          )}
        </div>
      );
    }
    case 'turn_start':
      return <div className="text-[10px] text-slate-400 dark:text-slate-600 text-center">— turn started —</div>;
    case 'turn_end':
      return (
        <div className="text-[10px] text-slate-400 dark:text-slate-600 text-center">
          — turn ended{c.cost ? ` · $${c.cost.toFixed(4)}` : ''}{c.isError ? ' · error' : ''} —
        </div>
      );
    case 'usage':
      return null; // not surfaced in the v1 UI
    case 'system':
      return <div className="text-[10px] text-slate-400 dark:text-slate-600 text-center font-mono">[{c.text}]</div>;
    case 'error':
      return (
        <div className="rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
          {c.message}
        </div>
      );
  }
}

function prettyJSON(v: unknown): string {
  try { return JSON.stringify(v, null, 2); }
  catch { return String(v); }
}
